import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Lock, RefreshCw, ScrollText } from 'lucide-react';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { formatDateTime, personName, useQueryParams } from './lib';

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  actorName: string | null;
  user: { id: string; firstName: string | null; lastName: string | null; email: string } | null;
}

interface StaffOption {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

/**
 * The models the trail is written against most often. The filter is a free-text
 * field underneath, because the interceptor stamps whatever entity a controller
 * declares and that list grows without this screen knowing.
 */
const COMMON_ENTITY_TYPES = [
  'Order',
  'Check',
  'Payment',
  'PolicyApproval',
  'StockMovement',
  'CatalogItem',
  'User',
  'Role',
  'Branch',
  'Device',
  'Reservation',
  'PurchaseOrder',
];

/** `yyyy-mm-dd` in the viewer's own timezone, which is what `<input type=date>` speaks. */
function toDayInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined) return null;
  return (
    <div className="min-w-0 flex-1">
      <p className="mb-1 text-xs font-medium text-ink-muted">{label}</p>
      <pre className="scrollbar-thin max-h-64 overflow-auto rounded-lg border bg-subtle p-2 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/**
 * §34 — the audit trail viewer.
 *
 * Read-only on purpose: the API has no create, update or delete for these rows,
 * because a trail that can be edited is not a trail. The screen says so rather
 * than leaving a reader to wonder where the delete button went.
 */
export default function AuditLog() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const [entityType, setEntityType] = useState('');
  const [entityTypeOther, setEntityTypeOther] = useState('');
  const [entityId, setEntityId] = useState('');
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState(() => toDayInput(new Date(Date.now() - 7 * 86_400_000)));
  const [to, setTo] = useState(() => toDayInput(new Date()));
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const effectiveEntityType = entityType === '__other' ? entityTypeOther.trim() : entityType;

  const params = useQueryParams({
    entityType: effectiveEntityType,
    entityId: entityId.trim(),
    userId,
    action: action.trim(),
    from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : '',
    page,
    limit: 50,
  });

  const { data, loading, error, reload } = useApi<unknown>('/audit', params);
  const { rows, total } = useMemo(() => rowsOf<AuditEntry>(data), [data]);

  // `GET /users` is gated on `users.view`; without it the actor filter simply
  // is not offered, rather than the screen failing to load.
  const staff = useApi<unknown>(can('users.view') ? '/users' : null, { limit: 200 });
  const staffRows = rowsOf<StaffOption>(staff.data).rows;

  const pages = Math.max(1, Math.ceil(total / 50));

  const resetTo = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ScrollText className="h-5 w-5" />
            {t('platform.auditLog.title')}
          </h1>
          <p className="text-sm text-ink-muted">
            {t('platform.auditLog.subtitle')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
          <RefreshCw className="h-4 w-4" />
          {t('platform.auditLog.refresh')}
        </Button>
      </header>

      <p className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-900 dark:text-amber-200">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        {t('platform.auditLog.appendOnly')}
      </p>

      <Card>
        <CardContent className="pt-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('platform.auditLog.entityType')}>
              <Select
                value={entityType}
                onChange={(e) => resetTo(() => setEntityType(e.target.value))}
              >
                <option value="">{t('platform.auditLog.allEntities')}</option>
                {COMMON_ENTITY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
                <option value="__other">{t('platform.auditLog.otherEntity')}</option>
              </Select>
            </Field>

            {entityType === '__other' && (
              <Field label={t('platform.auditLog.entityTypeOther')}>
                <Input
                  value={entityTypeOther}
                  onChange={(e) => resetTo(() => setEntityTypeOther(e.target.value))}
                  placeholder="ServiceRequest"
                />
              </Field>
            )}

            <Field label={t('platform.auditLog.entityId')}>
              <Input
                value={entityId}
                onChange={(e) => resetTo(() => setEntityId(e.target.value))}
                placeholder={t('platform.auditLog.entityIdHint')}
              />
            </Field>

            {can('users.view') && (
              <Field label={t('platform.auditLog.actor')}>
                <Select value={userId} onChange={(e) => resetTo(() => setUserId(e.target.value))}>
                  <option value="">{t('platform.auditLog.allActors')}</option>
                  {staffRows.map((person) => (
                    <option key={person.id} value={person.id}>
                      {personName(person)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field label={t('platform.auditLog.action')} hint={t('platform.auditLog.actionHint')}>
              <Input
                value={action}
                onChange={(e) => resetTo(() => setAction(e.target.value))}
                placeholder="policy.approval"
              />
            </Field>

            <Field label={t('platform.auditLog.from')}>
              <Input
                type="date"
                value={from}
                onChange={(e) => resetTo(() => setFrom(e.target.value))}
              />
            </Field>

            <Field label={t('platform.auditLog.to')}>
              <Input
                type="date"
                value={to}
                onChange={(e) => resetTo(() => setTo(e.target.value))}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {loading && !rows.length ? (
        <LoadingState label={t('platform.auditLog.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH className="w-8" />
                  <TH>{t('platform.auditLog.when')}</TH>
                  <TH>{t('platform.auditLog.actor')}</TH>
                  <TH>{t('platform.auditLog.action')}</TH>
                  <TH>{t('platform.auditLog.entity')}</TH>
                  <TH>{t('platform.auditLog.origin')}</TH>
                </TR>
              </THead>
              <tbody>
                {!rows.length && (
                  <TableEmpty colSpan={6}>
                    <EmptyState
                      title={t('platform.auditLog.empty')}
                      hint={t('platform.auditLog.emptyHint')}
                    />
                  </TableEmpty>
                )}

                {rows.map((entry) => {
                  const isOpen = expanded === entry.id;
                  const hasDetail = entry.before != null || entry.after != null;
                  return [
                    <TR key={entry.id}>
                      <TD>
                        {hasDetail && (
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-label={t('platform.auditLog.toggleDetail')}
                            onClick={() => setExpanded(isOpen ? null : entry.id)}
                            className="rounded p-1 hover:bg-hover"
                          >
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4 rtl:-scale-x-100" />
                            )}
                          </button>
                        )}
                      </TD>
                      <TD className="whitespace-nowrap text-ink-muted">
                        {formatDateTime(entry.createdAt, i18n.language)}
                      </TD>
                      <TD className="whitespace-nowrap">
                        {entry.actorName ?? personName(entry.user)}
                        {entry.user?.email && (
                          <div className="text-xs text-ink-muted">
                            {entry.user.email}
                          </div>
                        )}
                      </TD>
                      <TD>
                        <Badge tone="neutral">{entry.action}</Badge>
                      </TD>
                      <TD>
                        {entry.entityType}
                        {entry.entityId && (
                          <div className="text-xs text-ink-muted">
                            {entry.entityId}
                          </div>
                        )}
                      </TD>
                      <TD className="text-xs text-ink-muted">
                        {entry.ip ?? '—'}
                        {entry.userAgent && (
                          <div className="max-w-xs truncate" title={entry.userAgent}>
                            {entry.userAgent}
                          </div>
                        )}
                      </TD>
                    </TR>,
                    isOpen ? (
                      <tr key={`${entry.id}-detail`} className="border-b last:border-0">
                        <td colSpan={6} className="bg-subtle/60 p-3">
                          <div className="flex flex-col gap-3 md:flex-row">
                            <JsonBlock label={t('platform.auditLog.before')} value={entry.before} />
                            <JsonBlock label={t('platform.auditLog.after')} value={entry.after} />
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </Table>
          </TableWrap>

          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-ink-muted">
              {t('platform.auditLog.entryCount', { count: total })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('common.back')}
              </Button>
              <span className="tabular-nums">
                {t('platform.auditLog.pageOf', { page, pages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
